import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
} from "@mui/material";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { ShippingDocumentViewModel, ListQuery } from "../../../Types/ExportTypes";
import CenteredListCard from "../../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../../components/List/ListCardHeader";


import LoadingSection from "../../../components/LoadingSection";
import ResponsivePagination from "../../../components/ResponsivePagination";

const ShipmentList: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const [documents, setDocuments] = useState<ShippingDocumentViewModel[]>([]);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [query, setQuery] = useState<ListQuery>({
        page: 0,
        pageSize: 10,
        filter: "",
    });
    const [filter, setFilter] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: ShippingDocumentViewModel[]; count: number }>(
                `/shipmentDocumentApi/GetShipments`,
                query
            );
            setDocuments(response.items);
            setTotalDocuments(response.count);
        } catch (error) {
            console.error("Failed to fetch documents:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [query]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter: filter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setQuery({ ...query, pageSize: newPageSize, page: 0 });
    };

    const handleSelectDocument = (documentNo?: string | null) => {
        navigate({ path: "/sales/shipment/edit", params: { documentNo } });
    };



    return (
        <CenteredListCard title="Shipments">


            {/* Search Field */}
            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <TextField
                    label="Search Documents"
                    variant="outlined"
                    fullWidth
                    onChange={(event) => setFilter(event.target.value)}
                    value={filter}
                    disabled={loading}
                />
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading}
                >
                    Search
                </Button>
            </Box>

            {/* Card Layout for Orders */}
         <LoadingSection loading={loading}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {documents.map((document) => (
                    <Grid item
                        xs={12}
                        sm={documents.length === 1 ? 12 : documents.length === 2 ? 6 : 6}
                        md={documents.length === 1 ? 12 : documents.length === 2 ? 6 : 4}
                        key={document.no}>
                        <Card
                            onClick={() => handleSelectDocument(document.no)}
                            sx={{
                                cursor: "pointer",
                                transition: "box-shadow 0.2s ease-in-out",
                                "&:hover": {
                                    boxShadow: 4,
                                    backgroundColor: "secondary.main",
                                },
                            }}
                        >
                            <CardContent>
                                <ListCardHeader
                                    icon={<LocalShippingIcon fontSize="small" color="action" />}
                                    title={document.no}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    Customer No: {document.selltoCustomerNo}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Name: {document.shiptoName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Address:{document.shiptoAddress}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                {documents.length === 0 && (
                    <Grid item xs={12}>
                        <Typography align="center">No orders found</Typography>
                    </Grid>
                )}
            </Grid>
         </LoadingSection>

            <ResponsivePagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={totalDocuments}
                onPageChange={handlePageChange}
                sx={{ mt: 2 }}
            />
          
        </CenteredListCard>
    );
};

export default ShipmentList;